/**
 * Bulletproof audio playback for Gyro's voice (extracted from the Bridge reference implementation).
 *
 * Browsers block audio not tied to a user gesture, and a *muted* clip does NOT authorize later
 * *unmuted* playback. The reliable approach is the Web Audio API: create ONE AudioContext, resume it
 * on the first interaction (and prime it again on send), then play all TTS through it — a running
 * context can play any time without a fresh gesture. An HTMLAudioElement is the last-resort fallback.
 */
let ctx = null;
let fallbackEl = null;
let installed = false;
let playbackGeneration = 0;
let activeStop = null;
function getCtx() {
    if (ctx)
        return ctx;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC)
            return null;
        ctx = new AC();
        return ctx;
    }
    catch {
        return null;
    }
}
function unlock() {
    const c = getCtx();
    if (c && c.state === 'suspended')
        void c.resume().catch(() => undefined);
    if (c && c.state === 'running') {
        try {
            const buf = c.createBuffer(1, 1, 22050);
            const src = c.createBufferSource();
            src.buffer = buf;
            src.connect(c.destination);
            src.start(0);
        }
        catch {
            /* ignore */
        }
    }
    if (!fallbackEl) {
        try {
            fallbackEl = new Audio();
        }
        catch {
            /* ignore */
        }
    }
}
/** Install one-time global listeners that unlock audio on first interaction. Call once at app start. */
export function installAudioUnlock() {
    if (installed || typeof window === 'undefined')
        return;
    installed = true;
    const handler = () => unlock();
    for (const ev of ['pointerdown', 'keydown', 'touchstart', 'click']) {
        window.addEventListener(ev, handler, { passive: true });
    }
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden)
            unlock();
    });
}
/** Resume/warm the context from within a user gesture (e.g. on send) so a reply is never blocked. */
export function primeAudio() {
    unlock();
}
/** Play an audio Blob (e.g. ElevenLabs mp3). Web Audio first, then HTMLAudio fallback. */
export async function playAudioBlob(blob, options = {}) {
    if (options.signal?.aborted)
        return { ok: false, reason: 'cancelled' };
    if (!blob || blob.size === 0)
        return { ok: false, reason: 'no_audio' };
    stopPlayback();
    const generation = playbackGeneration;
    let finished = false;
    let source = null;
    let element = null;
    let objectUrl = null;
    const cancelled = () => options.signal?.aborted || generation !== playbackGeneration;
    const cleanup = (notify) => {
        if (finished)
            return;
        finished = true;
        options.signal?.removeEventListener('abort', stop);
        if (activeStop === stop)
            activeStop = null;
        if (source) {
            source.onended = null;
            try {
                source.stop();
            }
            catch { /* Already ended, or decoding never started. */ }
            try {
                source.disconnect();
            }
            catch { /* Already disconnected. */ }
            source = null;
        }
        if (element) {
            element.onended = null;
            element.onerror = null;
            try {
                element.pause();
                element.removeAttribute('src');
                element.load();
            }
            catch { /* Already released. */ }
            element = null;
        }
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }
        if (notify)
            options.onEnded?.();
    };
    const stop = () => cleanup(true);
    activeStop = stop;
    options.signal?.addEventListener('abort', stop, { once: true });
    const c = getCtx();
    if (c) {
        try {
            if (c.state === 'suspended')
                await c.resume();
            if (cancelled() || finished)
                return { ok: false, reason: 'cancelled' };
            if (c.state === 'running') {
                const arr = await blob.arrayBuffer();
                if (cancelled() || finished)
                    return { ok: false, reason: 'cancelled' };
                const audioBuf = await c.decodeAudioData(arr.slice(0));
                if (cancelled() || finished)
                    return { ok: false, reason: 'cancelled' };
                const src = c.createBufferSource();
                source = src;
                src.buffer = audioBuf;
                src.connect(c.destination);
                src.onended = () => cleanup(true);
                src.start(0);
                return { ok: true };
            }
        }
        catch {
            /* fall through to element */
        }
    }
    if (cancelled() || finished)
        return { ok: false, reason: 'cancelled' };
    if (source) {
        source.onended = null;
        try {
            source.stop();
            source.disconnect();
        }
        catch { /* A source that failed to start. */ }
        source = null;
    }
    try {
        const el = fallbackEl ?? (fallbackEl = new Audio());
        element = el;
        el.muted = false;
        objectUrl = URL.createObjectURL(blob);
        el.src = objectUrl;
        el.onended = () => cleanup(true);
        el.onerror = () => cleanup(true);
        await el.play();
        if (cancelled() || finished)
            return { ok: false, reason: 'cancelled' };
        return { ok: true };
    }
    catch {
        if (cancelled() || finished)
            return { ok: false, reason: 'cancelled' };
        cleanup(false);
        return { ok: false, reason: 'blocked' };
    }
}
/** Stop active playback and invalidate pending resume/decode/play work before it can produce sound. */
export function stopPlayback() {
    playbackGeneration++;
    const stop = activeStop;
    activeStop = null;
    stop?.();
}
export function audioReady() {
    return !!ctx && ctx.state === 'running';
}
//# sourceMappingURL=audio.js.map